import {useFluxStore} from 'client/framework';
import {Anchor, Heading, Icon, SplitCard} from '@albert-io/atomic';
import cx from 'classnames';
import {kebabCase} from 'lodash';
import React, {useRef, useState} from 'react';

import {PracticeSettings} from 'client/AdaptiveSubjects/AdaptiveSubjectsHeader/PracticeSettings/PracticeSettings';
import SubjectSplitCard from 'client/components/SubjectSplitCard/SubjectSplitCard.react';
import MarkdownRendererV2 from 'generic/MarkdownRendererV2/MarkdownRendererV2.react';
import masqueradeStore from 'generic/Masquerade/Masquerade.store';

interface AdaptiveSubjectCourseCardProps {
  className?: string;
  isLoggedOutOrStudent?: boolean;
  subjectId: string;
  subjectName: string;
  subjectUrlSlug: string;
  subjectDescription: string;
  curriculumAreaName: string;
  'data-testid'?: string;
}

export const AdaptiveSubjectCourseCard = ({
  className,
  isLoggedOutOrStudent,
  subjectId,
  subjectName,
  subjectUrlSlug,
  subjectDescription,
  curriculumAreaName,
  'data-testid': dataTestId
}: AdaptiveSubjectCourseCardProps) => {
  const [showDescription, setShowDescription] = useState(false);

  const linkRef = useRef<HTMLAnchorElement>(null);
  const descriptionRef = useRef<HTMLButtonElement>(null);

  const curriculumAreaSlug = kebabCase(curriculumAreaName);
  const isMasquerading = useFluxStore(masqueradeStore, (s) => s.isMasquerading());
  const showActionBar = !isLoggedOutOrStudent || isMasquerading;

  const subjectHref = `/adaptive-subjects/${subjectUrlSlug}`;

  // Generate dynamic test IDs based on exact subject name
  const getTestId = (suffix: string) =>
    dataTestId ? `${dataTestId}-${suffix}` : `${subjectName}-${suffix}`;

  const actionBar = (
    <Anchor role='switch' as='button' className='text-right font-bold' inheritColor>
      Practice Settings
      <Icon icon='caret-down' className='ml-x1' />
    </Anchor>
  );

  return (
    <SubjectSplitCard
      className={cx(className, {
        'subject-course-card--description-in-view': showDescription,
        'subject-course-card--action-bar-in-view': showActionBar
      })}
      key={subjectId}
    >
      <SubjectSplitCard.Top
        className={cx(
          `subject-course-card__details px-x3 py-x3 content-primary-inverse flex items-end overflow-hidden background-domain-${curriculumAreaSlug}`
        )}
        onClick={(e) => {
          const link = linkRef.current;
          const description = descriptionRef.current;
          if (link && link !== e.target && description !== e.target) {
            link.click();
          }
        }}
        data-testid={getTestId('card')}
      >
        <SubjectSplitCard.Title className='flex flex-col'>
          <Heading className={cx({'subject-course-card__title': !!subjectDescription})}>
            <Anchor
              // @ts-expect-error innerRef is a valid prop on react-router v3's Link, passed through Anchor's rest spread
              innerRef={linkRef}
              to={subjectHref}
              inheritColor
            >
              {subjectName}
            </Anchor>
          </Heading>
          <Anchor
            as='button'
            onClick={() => setShowDescription((prevShowDescription) => !prevShowDescription)}
            inheritColor
            className='subject-course-card__description-link'
            role='switch'
            aria-checked={showDescription}
            ref={descriptionRef}
            data-testid={getTestId('show-description-btn')}
          >
            {showDescription ? 'Hide description' : 'Show description'}
          </Anchor>
        </SubjectSplitCard.Title>
      </SubjectSplitCard.Top>
      <SplitCard.Section
        aria-hidden={!showDescription}
        className='subject-course-card__description px-x3! grow overflow-hidden'
      >
        <MarkdownRendererV2 text={subjectDescription} />
      </SplitCard.Section>
      {showActionBar && (
        <SubjectSplitCard.Bottom
          paddingLevel={0}
          border='none'
          className='subject-course-card__action-bar px-x3! py-x1 content-secondary flex justify-end'
        >
          <PracticeSettings
            subjectId={subjectId}
            trigger={actionBar}
            className='text-center font-normal'
            data-testid={getTestId('practice-settings-btn')}
          />
        </SubjectSplitCard.Bottom>
      )}
    </SubjectSplitCard>
  );
};
